// helpers.ts — Pure utility functions for D211 form

/** Convert ISO date string to HTML date input value (YYYY-MM-DD) */
export const dateToInput = (v: string | null | undefined): string => {
  if (!v) return '';
  return v.substring(0, 10);
};

/** Convert HTML date input value to ISO date string */
export const inputToDate = (v: string): string | null => {
  if (!v) return null;
  return v + 'T00:00:00Z';
};

/** Format number with locale grouping, max 2 decimals */
export const fmt = (v: number | null | undefined): string => {
  if (v == null || v === 0) return '0';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

/** Approval status label map (BR-010)
 *  統一狀態碼（FD110 與 pwd201f1/D501/D5M1 共表共碼）：
 *  ''=草稿、D=送審中、P=已核准、N=已沖銷 */
export const APPROVAL_STATUS: Record<string, string> = {
  '': '未送審',
  D: '送審中',
  P: '已核准',
  N: '已沖銷',
  '2': '部長已審核',
  '3': '處長已審核',
  '4': '課長已審核',
  '5': '組長已審核',
};

/** Close status label */
export const CLOSE_STATUS: Record<string, string> = {
  N: '',
  P: '已結案',
};

/** Material category labels */
export const MATERIAL_CATEGORY: Record<string, string> = {
  '1': '原料',
  '2': '半成品',
  '3': '成品',
};

/** Purchase category labels (for Combo2 購貨類別) */
export const PURCHASE_CATEGORY: Record<string, string> = {
  '1': '原料',
  '2': '半成品',
  '3': '成品',
};

/** Derive the status badge for a record */
export const getStatusBadge = (record: {
  fd110_80?: string;
  fd110_29?: string;
} | null): { text: string; cls: string } | null => {
  if (!record) return null;
  const s80 = (record.fd110_80 || '').trim();
  const s29 = (record.fd110_29 || '').trim();
  if (s80 === 'P' && s29 === 'P') return { text: '已結案', cls: 'badge-default' };
  if (s80 === 'P') return { text: '已核准', cls: 'badge-success' };
  // 統一狀態碼：D=送審中（容忍舊碼 S）、N=已沖銷
  if (s80 === 'D' || s80 === 'S') return { text: '送審中', cls: 'badge-warning' };
  if (s80 === 'N') return { text: '已沖銷', cls: 'badge-danger' };
  if (s80 === '2') return { text: '部長已審核', cls: 'badge-info' };
  if (s80 === '3') return { text: '處長已審核', cls: 'badge-info' };
  if (s80 === '4') return { text: '課長已審核', cls: 'badge-info' };
  if (s80 === '5') return { text: '組長已審核', cls: 'badge-info' };
  return null;
};

/** Check if record is locked for editing (BR-010)
 *  統一狀態碼：''=草稿才可編輯（FoxPro 規格 FD110_80 <> "N" 鎖定，"N" 舊義=未核准=新碼 ''） */
export const isLockedForEdit = (record: {
  fd110_80?: string;
  fd110_29?: string;
} | null): boolean => {
  if (!record) return false;
  const s80 = (record.fd110_80 || '').trim();
  const s29 = (record.fd110_29 || '').trim();
  return s80 !== '' || s29 === 'P';
};

/** Always-readonly fields */
const ALWAYS_READONLY = new Set([
  'fd110_01', 'fd110_23', 'fd110_24', 'fd110_91',
]);

/** Determine if a field should be disabled */
export const isFieldDisabled = (key: string, isEditing: boolean): boolean => {
  if (!isEditing) return true;
  if (ALWAYS_READONLY.has(key)) return true;
  return false;
};
